import React, { useRef, useState } from 'react';
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';

import { Building } from 'apolloClient/types/Building';
import { Listing } from 'apolloClient/types/Units';
import { EntityWithId } from 'apolloClient/types';
import { useFloorplanImage } from 'hooks/useFloorplanImage';
import { useTwoFingerZoom } from 'hooks/useTwoFingerZoom';

import UnitFloorplanBody from 'components/Floorplans/UnitFloorplanBody';
import BuildingFloorplanBodyV2 from './BuildingFloorplanBodyV2';
import RequestInfoBadgeV2 from 'componentsV2/RequestInfoBadgeV2';
import Loading from 'components/Loading';
import { defaultImage } from 'src/constants/constants';

interface FloorplanPopupV2BodyProps {
  initialSitePlan?: boolean;
  initialActiveRowIndex?: number;
  entity: Building | Listing;
  bodyType: 'listing' | 'building';
  showTable?: boolean;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  buildingFloorPlanImage?: Building['floorPlanImage'];
  onSelectRow?: (rowIndex: number) => void;
}

const MAX_PLAN_SCALE = 8;

type FloorplanTitleFields = {
  apartmentNumbers?: string;
  model?: string;
  bedBath?: string;
};

function getSelectedFloorplan(
  bodyType: FloorplanPopupV2BodyProps['bodyType'],
  entity: Building | Listing,
  listingFloorplan: FloorplanTitleFields | undefined,
  activeRowIndex: number | null
): FloorplanTitleFields | undefined {
  if (bodyType === 'listing') return listingFloorplan;
  if (activeRowIndex === null) return undefined;
  return (entity as Building).floorplans?.[activeRowIndex];
}

// e.g. "Apt# 04, Unit D, 2/3 + Den"; empty fields are skipped
function getFloorplanTitle(floorplan?: FloorplanTitleFields): string {
  if (!floorplan) return '';
  const { apartmentNumbers, model, bedBath } = floorplan;
  const aptLabel = apartmentNumbers ? `Apt# ${apartmentNumbers}` : '';
  return [aptLabel, model, bedBath].filter(Boolean).join(', ');
}

const FloorplanPopupV2: React.FC<FloorplanPopupV2BodyProps> = ({
  initialSitePlan = false,
  initialActiveRowIndex = null,
  entity,
  bodyType,
  showTable = true,
  scrollRef,
  buildingFloorPlanImage,
  onSelectRow,
}) => {
  const { activeRowIndex, floorplan, openImage, imageSrc } = useFloorplanImage({
    initialSitePlan,
    initialActiveRowIndex,
    entity,
    bodyType,
    buildingFloorPlanImage,
  });

  const zoomRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [prevImageSrc, setPrevImageSrc] = useState(imageSrc);
  // Picking another plan clears the failure, so reselecting it retries the URL
  if (prevImageSrc !== imageSrc) {
    setPrevImageSrc(imageSrc);
    setFailedSrc(null);
  }
  const displayedSrc = failedSrc === imageSrc ? defaultImage : imageSrc;
  const isImageLoading = loadedSrc !== displayedSrc;

  const floorplanTitle = initialSitePlan
    ? ''
    : getFloorplanTitle(
        getSelectedFloorplan(bodyType, entity, floorplan, activeRowIndex)
      );

  // Pan only while zoomed in, so a drag over the plan scrolls the pop-up instead
  function syncPanning() {
    const zoom = zoomRef.current;
    if (zoom) {
      zoom.instance.setup.panning.disabled =
        zoom.instance.transformState.scale <= 1;
    }
  }

  const planRef = useRef<HTMLDivElement>(null);
  useTwoFingerZoom(planRef, zoomRef, MAX_PLAN_SCALE);

  return (
    <div className="flex flex-col mb-36 md:mb-0 md:px-[30px] lg:h-full lg:min-h-0 lg:overflow-hidden lg:flex-row lg:gap-x-[50px] lg:px-[40px] relative">
      {floorplanTitle && (
        // Overlaps the panel's top padding, so the title sits in the gap above the plan
        <h3 className="lg:hidden mt-[-6px] mb-[9px] md:h-[50px] md:mt-[-50px] md:mb-0 md:leading-[50px] px-[20px] md:px-0 text-16-23-0.3 font-sohneKraftig truncate">
          {floorplanTitle}
        </h3>
      )}
      <div
        ref={planRef}
        className={`relative w-full touch-pan-y lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain ${
          showTable ? 'lg:flex-1 lg:min-w-0' : 'lg:max-w-[860px] lg:mx-auto'
        } ${isImageLoading ? 'min-h-[240px]' : ''}`}
        onMouseDownCapture={syncPanning}
      >
        <TransformWrapper
          initialScale={1}
          maxScale={MAX_PLAN_SCALE}
          centerZoomedOut={true}
          // A plain mouse wheel scrolls; trackpad pinch and Ctrl+wheel still zoom
          wheel={{ wheelDisabled: true }}
          panning={{ disabled: true }}
          pinch={{ disabled: true }}
          onInit={(ref) => {
            zoomRef.current = ref;
          }}
        >
          <TransformComponent
            wrapperStyle={{ width: '100%' }}
            contentStyle={{ width: '100%' }}
          >
            <img
              className="block w-full h-auto"
              src={displayedSrc}
              alt="image-floorplan"
              onLoad={() => setLoadedSrc(displayedSrc)}
              onError={() => {
                // Show the "no image" placeholder instead of a broken image;
                // if that fails too, stop loading so the overlay can't get stuck
                if (displayedSrc === defaultImage) setLoadedSrc(displayedSrc);
                else setFailedSrc(imageSrc);
              }}
            />
          </TransformComponent>
        </TransformWrapper>
        {isImageLoading && <Loading overlay label="Loading floor plan" />}
      </div>
      <div
        className={`flex flex-col w-full max-w-[1368px] mx-auto px-[20px] md:px-0 mt-16 md:mt-[50px] lg:mt-0 lg:h-full lg:min-h-0 lg:flex-none lg:overflow-hidden ${
          showTable ? 'lg:w-[400px] xl:w-[450px]' : 'md:hidden'
        }`}
      >
        <div className="md:hidden shrink-0">
          <RequestInfoBadgeV2 isSticky={true} />
        </div>
        <div className="lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
          {bodyType === 'building' && showTable && (
            <BuildingFloorplanBodyV2
              building={entity as Building}
              activeRowIndex={activeRowIndex}
              openImage={openImage}
              scrollRef={scrollRef}
              onSelectRow={onSelectRow}
            />
          )}
          {bodyType === 'listing' && showTable && (
            <UnitFloorplanBody
              floorplan={floorplan}
              listing={entity as EntityWithId<Listing>}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FloorplanPopupV2;
